'use client';

import { Button } from '@ui/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@ui/components/ui/dialog';
import { Textarea } from '@ui/components/ui/textarea';
import { useState } from 'react';
import { Sentiment, submitFeedback, submitWorkflowOutputFeedback } from '@/app/(protected)/feedback/actions';
import { useTranslations } from 'next-intl';
import { toast } from '@ui/components/ui/sonner';

interface FeedbackDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  outputFeedback?: {
    sentiment: Sentiment;
    outputFieldKey: string;
    outputFieldTitle: string;
    productTitle: string;
  };
}

export function FeedbackDialog({ open, onOpenChange, outputFeedback }: FeedbackDialogProps) {
  const t = useTranslations('Feedback');
  const tCommon = useTranslations('Common.actions');
  const [feedback, setFeedback] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    setIsSubmitting(true);

    try {
      if (outputFeedback) {
        // Submit workflow output feedback with user info
        await submitWorkflowOutputFeedback({
          productTitle: outputFeedback.productTitle,
          outputFieldTitle: outputFeedback.outputFieldTitle,
          sentiment: outputFeedback.sentiment,
          feedback: feedback.trim(),
        });
      } else {
        // Submit general feedback
        if (!feedback.trim()) {
          toast.error(t('error.empty'));
          setIsSubmitting(false);
          return;
        }
        await submitFeedback(feedback);
      }

      toast.success(t('success'));
      setFeedback('');
      onOpenChange(false);
    } catch (error) {
      console.error('Error submitting feedback:', error);
      toast.error(t('error.submit'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>{t('title')}</DialogTitle>
            <DialogDescription>{outputFeedback ? t('workflowDescription') : t('description')}</DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Textarea
              placeholder={t('placeholder')}
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              rows={6}
              className="resize-none"
              disabled={isSubmitting}
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
              {tCommon('cancel')}
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? tCommon('submitting') : tCommon('submit')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
